import { Dialog, Modal, ModalOverlay } from "@admin-ui/components/primitives/application/modals/modal";
import type { ReloadOptions } from "@inertiaui/modal-react";
import type { ReactNode } from "react";
import { RoutedModalBridge, type RoutedModalBridgeRenderProps } from "@/components/RoutedModalBridge/RoutedModalBridge";
import { useModalLeaveAnimation } from "@/hooks/use-modal-leave-animation";

interface AdminRoutedModalRenderProps {
  close: () => void;
  emit: (event: string, ...args: unknown[]) => void;
  reload: (options?: ReloadOptions) => void;
}

export interface AdminRoutedModalProps {
  children: (props: AdminRoutedModalRenderProps) => ReactNode;
}

/**
 * Bridge between Inertia Modal's HeadlessModal and admin-ui modal primitives.
 * Admin modals have custom layouts (no shared Modal header/body/footer slots), so
 * the children render function receives { close } and renders its own UI.
 */
export function AdminRoutedModal({ children }: AdminRoutedModalProps) {
  return (
    <RoutedModalBridge>
      {(bridge) => <AdminRoutedModalShell {...bridge}>{children}</AdminRoutedModalShell>}
    </RoutedModalBridge>
  );
}

interface AdminRoutedModalShellProps extends RoutedModalBridgeRenderProps {
  children: AdminRoutedModalProps["children"];
}

/** Inner component so the leave-animation hook can run on the bridge props. */
function AdminRoutedModalShell({
  afterLeave,
  children,
  close,
  emit,
  isOpen,
  onOpenChange,
  reload,
}: AdminRoutedModalShellProps) {
  const handleAnimationEnd = useModalLeaveAnimation(isOpen, afterLeave);

  return (
    <ModalOverlay isOpen={isOpen} onOpenChange={onOpenChange} onAnimationEnd={handleAnimationEnd} isDismissable>
      <Modal>
        <Dialog>{children({ close, emit, reload })}</Dialog>
      </Modal>
    </ModalOverlay>
  );
}
